import { useMemo } from 'react'

import { useCommunityConfig } from '../../CommunityConfig'
import type { EngagementType, Service } from '../../graphqlEnums'

import type { NoMediaChannel } from './noMediaCopy'

type MediaSupportChannel = {
  id: string
  service: Service
}

/**
 * The channels in scope that can't surface media for `engagementType`, paired
 * with the copy explaining it. Feeds {@link NoMediaHint}.
 *
 * A channel is affected only when the backend says so — the set of networks
 * that strip media grows and shrinks without a frontend change. A channel with
 * no media configuration is treated as supporting media, so a missing entry
 * never invents a warning, and one missing its copy is skipped rather than
 * rendered blank.
 */
export const useNoMediaChannels = (
  channels: MediaSupportChannel[],
  engagementType: EngagementType,
): NoMediaChannel[] => {
  const { config } = useCommunityConfig()

  return useMemo(
    () =>
      channels.flatMap((channel) => {
        const media = config.getMediaConfiguration(channel.id, engagementType)

        if (!media || media.supportsMedia) return []
        if (!media.disclaimer || !media.additionalInformation) return []

        return [
          {
            service: channel.service,
            disclaimer: media.disclaimer,
            additionalInformation: media.additionalInformation,
          },
        ]
      }),
    [channels, config, engagementType],
  )
}
